import {
  api,
  startLoading,
  endLoading
} from '@/api'
import * as types from './types'

function isLogoFormatValid(logo) {
  // Check if selected images has valid extensions
  for (let i = 0; i < logo.length; i++) {
    var ext = logo[i].name.match(/\.([^.]+)$/)[1]

    // Return false if extension is invalid
    if (ext.match(/(jpg|jpeg|png)/) === null) {
      return false
    }
  }
  return true
}

export default {
  /**
   * Reset location values to its defaults
   */
  resetLocationValues({
    commit
  }) {
    commit(types.RESET_LOCATION)
  },
  /**
   * Get states
   * @param {Number} locationID - Location ID
   */
  async getStates({
    commit,
    dispatch
  }) {
    let loaderName = 'getStates'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .getStates()
        .then((response) => {
          commit(types.SET_STATES, response.data)
          endLoading(dispatch, loaderName)
          resolve(response.data)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
   * Get location by id
   * @param {Number} locationID - Location ID
   */
  async getLocation({
    commit,
    dispatch
  }, locationID) {
    let loaderName = 'getLocation'
    startLoading(dispatch, loaderName)
    return new Promise((resolve, reject) => {
      api
        .getLocation(locationID)
        .then((response) => {
          commit(types.SET_LOCATION, response.data)
          endLoading(dispatch, loaderName)
          resolve(response.data)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
   * Get all locations by based on user
   * @param {Object} params - SearchOptions object
   */
  async getLocationsByUser({
    commit,
    dispatch
  }, params) {
    let loaderName = 'getLocationsByUser'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      api
        .getLocationsByUser(params)
        .then((response) => {
          commit(types.SET_LOCATIONS, response.data.items)
          endLoading(dispatch, loaderName)
          resolve(response.data.count)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
   * Create new location
   */
  async createLocation({
    commit,
    dispatch,
    state
  }) {
    let loaderName = 'createLocation'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      // Prepare FormData
      let formData = new FormData()
      formData.append('name', state.location.name)
      formData.append('accountNumber', state.location.accountNumber)
      formData.append('stateID', state.location.stateID)

      // Append photos to FormData
      if (state.location.logo !== null && state.location.logo.length > 0) {
        if (isLogoFormatValid(state.location.logo)) {
          if (state.location.logo[0].size > 50000) {
            reject(new Error('File is too big. (Limit 50kb)'))
            endLoading(dispatch, loaderName)
            return
          }
          for (let i = 0; i < state.location.logo.length; i++) {
            formData.append('logo', state.location.logo[i])
          }
        } else {
          state.location.logo = []
          reject(
            new Error(
              'The selected file has an invalid extension. Please make sure you select a file with extension JPG, JPEG or PNG.'
            )
          )
          endLoading(dispatch, loaderName)
          return
        }
      }

      // Call API
      api
        .createLocation(formData)
        .then((response) => {
          // Note: If there's no data, it means that the location already exists
          if (response.data !== '') {
            commit(types.ADD_LOCATION, response.data)
            resolve(response)
          } else {
            reject(new Error('Location already exists.'))
          }
          endLoading(dispatch, loaderName)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
   * Update existing location
   */
  async updateLocation({
    commit,
    dispatch,
    state
  }) {
    let loaderName = 'updateLocation'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      // Prepare FormData
      let formData = new FormData()
      formData.append('id', state.location.id)
      formData.append('name', state.location.name)
      formData.append('accountNumber', state.location.accountNumber)
      formData.append('stateID', state.location.stateID)

      if (state.location.logo === [] && state.location.logoBase64 !== null) {
        formData.append('updateLogo', 0)
      } else {
        formData.append('updateLogo', 1)
      }

      // Append photos to FormData
      if (state.location.logo !== []) {
        if (isLogoFormatValid(state.location.logo)) {
          if (state.location.logo.size > 500000) {
            reject(new Error('File is too big. (Limit 500kb)'))
            endLoading(dispatch, loaderName)
            return
          }
          formData.append('logo', state.location.logo)
        } else {
          state.location.logo = []
          reject(
            new Error(
              'The selected file has an invalid extension. Please make sure you select a file with extension JPG, JPEG or PNG.'
            )
          )
          endLoading(dispatch, loaderName)
          return
        }
      }

      // Call API
      api
        .updateLocation(formData)
        .then((response) => {
          if (response.data === -1) {
            reject(new Error('Location name already exists.'))
          }
          resolve(response)
          endLoading(dispatch, loaderName)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
   * Delete an existing location
   */
  async deleteLocation({
    commit,
    dispatch
  }, location) {
    let loaderName = 'deleteLocation'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      // Call API
      api
        .deleteLocation({
          id: location.id
        })
        .then((response) => {
          commit(types.DELETE_LOCATION, location)
          endLoading(dispatch, loaderName)
          resolve(response)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  },
  /**
   * Get all locations by based on user
   * @param {Object} params - SearchOptions object
   */
  async getReportKPI({
    commit,
    dispatch
  }, locationID) {
    let loaderName = 'getReportKPI'
    startLoading(dispatch, loaderName)

    return new Promise((resolve, reject) => {
      api
        .getReportKPI(locationID)
        .then((response) => {
          commit(types.SET_KPI_STATS, response.data)
          endLoading(dispatch, loaderName)
          resolve(response.data)
        })
        .catch((error) => {
          endLoading(dispatch, loaderName)
          reject(error)
        })
    })
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/location/actions.js